/* eslint-disable max-len */
import DOMPurify from 'dompurify';
export const getOffset = function (el) {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY
  };
};
export const hasSomeParentTheClass = function (element, classname) {
  if (element.className.split(' ').indexOf(classname) >= 0) return true;
  return element.parentNode && hasSomeParentTheClass(element.parentNode, classname);
};
export const resetHeight = $elements => {
  $elements.forEach($el => {
    $el.style.height = 'auto';
  });
};
export const maxHeight = function ($elements, resHeight = true) {
  if (!$elements) return;
  if (resHeight) resetHeight($elements);
  const max = Math.max.apply(null, [...$elements].map($el => $el.offsetHeight));
  $elements.forEach($el => {
    $el.style.height = `${max}px`;
  });
};
export const minHeight = function ($elements, resHeight = true) {
  if (!$elements) return;
  if (resHeight) resetHeight($elements);
  const min = Math.min.apply(null, [...$elements].map($el => $el.offsetHeight));
  $elements.forEach($el => {
    $el.style.height = `${min}px`;
  });
};

/**
 * Convert bytes to more proper file size.
 * @param {number} bytes - file size in bites.
 * @param {number} decimals - return file size in number size.
 * @returns {string} File size.
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // eslint-disable-next-line no-mixed-operators
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Creates an HTML element.
 * @param {string} tag - The HTML tag name.
 * @param {Object} attributes - An object of attributes to set on the element.
 * @param {string} content - The content of the element.
 * @param {boolean} isHtml - If true, treats content as HTML and sanitizes it using DOMPurify.
 * @returns {HTMLElement} The created element.
 */
export const createEl = (tag = 'div', attributes = {}, content = '', isHtml = false) => {
  const $el = document.createElement(tag);
  Object.keys(attributes).forEach(attr => {
    $el.setAttribute(attr, attributes[attr]);
  });
  if (isHtml) {
    $el.innerHTML = DOMPurify.sanitize(content);
  } else {
    $el.textContent = content;
  }
  return $el;
};

/**
 * Delays invoking `func` until `delay` ms have passed since the last call.
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Delay in milliseconds.
 * @returns {Function} The debounced function.
 */
export const debounce = (func, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Pushes an event to the GTM dataLayer, creating it if it doesn't exist yet.
 * @param {Object} payload - The event payload, e.g. { event: 'name', ...params }.
 */
export const pushDataLayerEvent = payload => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};
export const arrayEquals = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
export const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

/**
 * Dispatches a custom event.
 * Consider validating 'eventName' if it can be dynamic and influenced by untrusted input.
 * @param {Element} element - The element to dispatch the event from.
 * @param {string} eventName - The name of the event.
 */
export const dispatchEvent = function (element, eventName) {
  if ('createEvent' in document) {
    const event = document.createEvent('HTMLEvents');
    event.initEvent(eventName, false, true);
    element.dispatchEvent(event);
  } else {
    // Deprecated for modern browsers, only for old IE
    element.fireEvent(eventName);
  }
};
export const getElementPositionInContainer = (element, container) => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return {
    left: elementRect.left - containerRect.left + container.scrollLeft,
    top: elementRect.top - containerRect.top + container.scrollTop
  };
};
export const isInViewport = (element, additionalMargin) => {
  const $header = document.querySelector('.header');
  const headerOffsetHeight = $header.offsetHeight;
  const navHeight = headerOffsetHeight + additionalMargin;
  const rect = element.getBoundingClientRect();
  const {
    offsetHeight,
    offsetWidth
  } = element;
  const clientHeight = window.innerHeight || document.documentElement.clientHeight;
  const clientWidth = window.innerWidth || document.documentElement.clientWidth;
  return rect.top >= -offsetHeight + navHeight && rect.left >= -offsetWidth && rect.bottom <= clientHeight + offsetHeight + navHeight && rect.right <= clientWidth + offsetWidth;
};
export const elementIsVisibleInViewport = (el, partiallyVisible = false, additionalMargin = 0) => {
  const $header = document.querySelector('.header');
  const headerOffsetHeight = $header.offsetHeight;
  const offset = headerOffsetHeight + additionalMargin;
  const rect = el.getBoundingClientRect();
  let {
    top,
    bottom
  } = rect;
  const {
    left,
    right
  } = rect;
  const {
    innerHeight,
    innerWidth
  } = window;
  top -= offset;
  bottom -= offset;
  return partiallyVisible ? (top > 0 && top < innerHeight || bottom > 0 && bottom < innerHeight) && (left > 0 && left < innerWidth || right > 0 && right < innerWidth) : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};
export const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

/**
 * Get element in dom tree or some element.
 * @param {string} selector - Selector of some element.
 * @param {Document|Element} env - Selector parent element.
 * @returns {Element} The found element.
 */
export const getElement = (selector, env = document) => env.querySelector(selector);

/**
 * Get multiply elements in dom tree or some element.
 * @param {string} selector - Selector of some element.
 * @param {Document|Element} env - Selector parent element.
 * @returns {Array} The found elements.
 */
export const getElements = (selector, env = document) => [...env.querySelectorAll(selector)];
const unifyClassName = className => Array.isArray(className) ? className : [className];

/**
 * Add CSS class to an element.
 * @param {Element} el - The target element.
 * @param {string} className - The CSS class name to add.
 */
export const addClass = (el, className) => el.classList.add(...unifyClassName(className));

/**
 * Remove CSS class from an element.
 * @param {Element} el - The target element.
 * @param {string} className - The CSS class name to add.
 */
export const removeClass = (el, className) => el.classList.remove(...unifyClassName(className));

/**
 * Toggle CSS class on an element.
 * @param {Element} el - The target element.
 * @param {string} className - The CSS class name to toggle.
 * @param {boolean} [condition] - If true, adds the class. If false, removes it. If omitted, toggles current state.
 */
export const toggleClass = (el, className, condition) => el.classList.toggle(...unifyClassName(className), condition);

/**
 * Check if an element has some CSS class name.
 * @param {Element} el - The target element.
 * @param {string} className - The CSS class name to add.
 */
export const hasClass = (el, className) => el.classList.contains(...unifyClassName(className));
export const getSiblings = el => {
  const parent = el.parentNode;
  const {
    children
  } = parent;
  return Array.from(children).filter(child => child !== el);
};

/**
 * Handles clicks outside a specified element.
 * Ensure 'callback' is a trusted function if it can be influenced by untrusted input.
 * @param {Element} element - The element to detect clicks outside of.
 * @param {Element|null} exception - An element that, if clicked, should not trigger the callback.
 * @param {boolean} condition - A condition that must be true for the callback to execute.
 * @param {Function|null} callback - The function to execute when an outside click occurs.
 */
export const clickOutside = (element, exception = null, condition = false, callback = null) => {
  const outsideClickHandler = event => {
    const withinBoundaries = event.composedPath().includes(element);
    if (!withinBoundaries && condition && callback || event.target === exception) {
      callback();
      document.removeEventListener('click', outsideClickHandler);
    }
  };
  const timeout = setTimeout(() => {
    document.addEventListener('click', outsideClickHandler);
    clearTimeout(timeout);
  }, 0);
};
export const isWindows = () => {
  const {
    platform
  } = navigator;
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  if (windowsPlatforms.indexOf(platform) !== -1) {
    addClass(document.firstElementChild, '__win');
  }
};

/**
 * Generates a simple, non-cryptographically secure unique ID.
 * DO NOT use for security-sensitive purposes (e.g., session tokens, cryptographic keys).
 * @param {string} prefix - A prefix for the ID.
 * @returns {string} A unique ID.
 */
export const generateId = (prefix = '') => prefix + Math.random().toString(36).substr(2, 6);

/**
 * Sets the value of a CSS custom property (variable) on an element.
 * @param {string} property - The name of the CSS property (e.g., '--my-color').
 * @param {string|number} value - The value to set for the property.
 * @param {Element} [element=document.documentElement] - The element to set the variable on. Defaults to the root element.
 */
export const setCssVariable = (property, value, element = document.documentElement) => {
  element.style.setProperty(property, value);
};

/**
 * Gets the value of a CSS custom property (variable) from an element.
 * @param {string} property - The name of the CSS property (e.g., '--my-color').
 * @param {Element} [element=document.documentElement] - The element to get the variable from. Defaults to the root element.
 * @returns {string} The value of the CSS property.
 */
export const getCssVariable = (property, element = document.documentElement) => getComputedStyle(element).getPropertyValue(property).trim();